import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, MessageSquare, CheckCircle, Mail, Send, RefreshCw, User, Mail as MailIcon } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';

interface SupportTicketData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  ticket_id?: string;
}

interface ProfileInfo {
  name?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}

const CustomerSupport: React.FC = () => {
  const { apiCall, user } = useAuth();
  const { t } = useTranslation('common');
  
  // Support Ticket State
  const [supportTicket, setSupportTicket] = useState<SupportTicketData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportResult, setSupportResult] = useState<ApiResponse | null>(null);
  const [profileInfo, setProfileInfo] = useState<ProfileInfo>({});

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load profile info for prefilling
  useEffect(() => {
    let isMounted = true;
    
    const loadProfileInfo = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
        const response = await apiCall(`${apiBaseUrl}/api/v1/profile-info`, {
          method: 'GET'
        });
        if (response.ok && isMounted) {
          const data = await response.json();
          setProfileInfo(data);
          
          // Prefill form with profile data
          setSupportTicket(prev => ({
            ...prev,
            email: data.email || ''
          }));
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error loading profile info:', error);
        }
      }
    };

    if (user && !profileInfo.name) {
      loadProfileInfo();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.userId]); // Only depend on user ID, not the whole user object

  // Submit Support Ticket
  const submitSupportTicket = async () => {
    if (!supportTicket.email || !supportTicket.subject || !supportTicket.message) {
      alert(t('support.errorRequired'));
      return;
    }

    setSupportLoading(true);
    setSupportResult(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await apiCall(`${apiBaseUrl}/api/v1/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(supportTicket),
      });

      const result = await response.json();
      setSupportResult(result);

      if (result.success) {
        // Reset form after successful submission
        setSupportTicket({
          name: profileInfo.name || profileInfo.first_name || '',
          email: profileInfo.email || '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting support ticket:', error);
      setSupportResult({
        success: false,
        message: t('support.errorSubmit')
      });
    } finally {
      setSupportLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-rose-50">
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-16 py-3 sm:py-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-pink-600" />
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900">{t('support.title')}</h1>
            </div>
            
            <div className="flex items-center">
              <div className="text-xs sm:text-sm text-gray-600">
                {t('support.subtitle')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        
        {/* Support Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-xl shadow-sm p-4 sm:p-8"
        >
          <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
            <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-pink-600" />
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('support.contactTitle')}</h2>
              <p className="text-sm text-gray-600">{t('support.contactSubtitle')}</p>
            </div>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {/* Email */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <MailIcon className="inline w-4 h-4 mr-1" />
                {t('support.emailLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={supportTicket.email}
                onChange={(e) => setSupportTicket({ ...supportTicket, email: e.target.value })}
                placeholder={t('support.emailPlaceholder')}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                maxLength={254}
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                {t('support.subjectLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={supportTicket.subject}
                onChange={(e) => setSupportTicket({ ...supportTicket, subject: e.target.value })}
                placeholder={t('support.subjectPlaceholder')}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base"
                maxLength={200}
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                {t('support.messageLabel')} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={supportTicket.message}
                onChange={(e) => setSupportTicket({ ...supportTicket, message: e.target.value })}
                placeholder={t('support.messagePlaceholder')}
                rows={5}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm sm:text-base resize-none"
                maxLength={5000}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('support.messageHint')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end pt-3 sm:pt-4">
              <button
                onClick={submitSupportTicket}
                disabled={supportLoading || !supportTicket.email || !supportTicket.subject || !supportTicket.message}
                className="flex items-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-lg hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-sm sm:text-base"
              >
                {supportLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
                <span>{supportLoading ? t('support.sendingButton') : t('support.sendButton')}</span>
              </button>
            </div>

            {/* Result */}
            {supportResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg ${
                  supportResult.success 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <div className="flex items-center space-x-2">
                  {supportResult.success ? (
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                  )}
                  <span className={`text-sm sm:text-base font-medium ${
                    supportResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {supportResult.message}
                  </span>
                </div>
                {supportResult.ticket_id && (
                  <p className="text-sm text-green-600 mt-1">
                    {t('support.ticketId')} {supportResult.ticket_id}
                  </p>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CustomerSupport;
